// src/components/DeepFikaAdmin.jsx
// Admin dashboard for Deep Fika (1-on-1 matched fika)
// Filters curated_fika_applications where format_type = 'deep_fika'
// Reuses curatedFikaAdminService — no new backend needed

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import curatedFikaAdminService from '../services/curatedFikaAdminService';
import {
  UserGroupIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  EnvelopeIcon,
  CalendarIcon,
  EyeIcon,
  PencilIcon,
  SparklesIcon,
  StarIcon,
  HeartIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  ArrowPathIcon,
  BoltIcon
} from '@heroicons/react/24/outline';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  submitted:    'bg-blue-100 text-blue-800',
  under_review: 'bg-yellow-100 text-yellow-800',
  approved:     'bg-emerald-100 text-emerald-800',
  rejected:     'bg-red-100 text-red-800',
  waitlist:     'bg-gray-100 text-gray-800',
};

const STATUS_LABELS = {
  submitted:    'Submitted',
  under_review: 'Under Review',
  approved:     'Approved',
  rejected:     'Rejected',
  waitlist:     'Waitlist',
};

function StatusBadge({ status }) {
  return (
    <span className={'px-2 py-1 rounded-full text-xs font-semibold ' + (STATUS_COLORS[status] || 'bg-gray-100 text-gray-700')}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function getSubscriptionDisplay(app) {
  if (!app.subscription_plan_name) {
    return { label: 'No subscription', color: 'bg-gray-100 text-gray-500', icon: '⚫' };
  }
  const planDisplay = { month: 'Monthly', '6_months': '6-Month', year: 'Yearly' };
  const colors = { month: 'bg-blue-100 text-blue-700', '6_months': 'bg-purple-100 text-purple-700', year: 'bg-green-100 text-green-700' };
  const icons = { month: '🔵', '6_months': '🟣', year: '🟢' };
  const interval = app.subscription_billing_interval || 'month';
  return {
    label: app.is_trial ? (planDisplay[interval] || 'Premium') + ' (Trial)' : (planDisplay[interval] || 'Premium'),
    color: colors[interval] || 'bg-blue-100 text-blue-700',
    icon: icons[interval] || '💎',
  };
}

function formatSubscriptionExpiry(expiryDate) {
  if (!expiryDate) return null;
  const expiry = new Date(expiryDate);
  const now = new Date();
  const days = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
  const formatted = expiry.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
  if (days < 0) return { text: 'Expired ' + formatted, color: 'text-red-600' };
  if (days <= 7) return { text: formatted + ' (' + days + 'd)', color: 'text-orange-600' };
  if (days <= 30) return { text: formatted + ' (' + days + 'd)', color: 'text-yellow-600' };
  return { text: formatted, color: 'text-gray-500' };
}

function StatCard({ label, value, icon, color = 'emerald' }) {
  const colorMap = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    blue:    'bg-blue-50 text-blue-700 border-blue-200',
    amber:   'bg-amber-50 text-amber-700 border-amber-200',
    gray:    'bg-gray-50 text-gray-700 border-gray-200',
  };
  return (
    <div className={'rounded-xl border p-4 ' + colorMap[color]}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-2xl font-bold">{value ?? '—'}</div>
          <div className="text-sm mt-0.5">{label}</div>
        </div>
        <div className="text-3xl opacity-40">{icon}</div>
      </div>
    </div>
  );
}

// ─── Application Detail Modal ─────────────────────────────────────────────────

function ApplicationModal({ app, onClose, onStatusChange }) {
  const [newStatus, setNewStatus] = useState(app.application_status);
  const [notes, setNotes] = useState(app.internal_notes || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await curatedFikaAdminService.updateApplicationStatus(app.id, newStatus, notes);
      onStatusChange(app.id, newStatus, notes);
      onClose();
    } catch (err) {
      alert('Error updating status: ' + err.message);
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-gray-900">
              {app.first_name} {app.last_name}
            </h3>
            <p className="text-sm text-gray-500">{app.email}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XCircleIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Quick info */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            {[
              ['Age range', app.age_range],
              ['Nationality', app.nationality],
              ['Profession', app.profession],
              ['Industry', app.industry],
              ['City', app.city],
              ['Schedule', app.schedule_preference],
              ['Location', app.location_preference],
              ['Communication', app.communication_style],
            ].map(([label, val]) => val ? (
              <div key={label}>
                <span className="text-gray-500">{label}: </span>
                <span className="text-gray-900 font-medium">{val}</span>
              </div>
            ) : null)}
          </div>

          {app.interests?.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Interests</div>
              <div className="flex flex-wrap gap-1">
                {app.interests.map(i => (
                  <span key={i} className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs rounded-full border border-emerald-200">
                    {i}
                  </span>
                ))}
              </div>
            </div>
          )}

          {app.fika_goals?.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Fika Goals</div>
              <div className="flex flex-wrap gap-1">
                {app.fika_goals.map(g => (
                  <span key={g} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full border border-blue-200">
                    {g}
                  </span>
                ))}
              </div>
            </div>
          )}

          {app.languages_spoken?.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Languages</div>
              <p className="text-sm text-gray-700">{app.languages_spoken.join(', ')}</p>
            </div>
          )}

          {app.additional_info && (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Additional Info</div>
              <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3">{app.additional_info}</p>
            </div>
          )}

          {/* Status change */}
          <div className="border-t border-gray-100 pt-5">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Update Status</div>
            <select
              value={newStatus}
              onChange={e => setNewStatus(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            >
              <option value="submitted">Submitted</option>
              <option value="under_review">Under Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="waitlist">Waitlist</option>
            </select>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Internal notes (optional)..."
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Create Session Modal (mutual acceptance model) ───────────────────────────
// Admin picks 2 people + optional note → invitations sent → users accept each
// other → contact info shared → they coordinate venue/time themselves.

function CreateSessionModal({ approvedApps, onClose, onCreated }) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [adminNote, setAdminNote] = useState('');
  const [creating, setCreating] = useState(false);

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id)
        ? prev.filter(x => x !== id)
        : prev.length < 2 ? [...prev, id] : prev
    );
  };

  const selectedApps = approvedApps.filter(a => selectedIds.includes(a.id));

  const handleSendInvitations = async () => {
    if (selectedIds.length !== 2) {
      alert('Please select exactly 2 applicants to match.');
      return;
    }
    setCreating(true);
    try {
      const result = await curatedFikaAdminService.sendDeepFikaInvitations(selectedIds, adminNote);
      if (result.success) {
        onCreated(result);
        onClose();
      } else {
        throw new Error(result.error || 'Unknown error');
      }
    } catch (err) {
      alert('Error sending invitations: ' + err.message);
    }
    setCreating(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Create Deep Fika Match</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Match invitations are sent to both people. They accept each other, then coordinate the meet themselves.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XCircleIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* How it works */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-800">
            <div className="font-semibold mb-2">How this works</div>
            <ol className="space-y-1 text-xs text-emerald-700 list-decimal list-inside">
              <li>Both people receive a match invitation email with each other's profile</li>
              <li>Each clicks Accept (or Decline) within 7 days</li>
              <li>When both accept, contact info (WhatsApp / LINE / phone) is shared</li>
              <li>They coordinate venue and time themselves</li>
              <li>Feedback email sent automatically 14 days after mutual acceptance</li>
            </ol>
          </div>

          {/* Pick 2 people */}
          <div>
            <div className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-3">
              Select 2 applicants
              {selectedIds.length > 0 && (
                <span className="ml-2 text-emerald-700 normal-case">{selectedIds.length}/2 selected</span>
              )}
            </div>

            {approvedApps.length === 0 ? (
              <div className="text-center py-10 text-gray-400">
                <HeartIcon className="w-10 h-10 mx-auto mb-2 text-gray-200" />
                <p className="text-sm">No approved applicants yet.</p>
                <p className="text-xs mt-1">Approve applications from the Applications tab first.</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {approvedApps.map(app => {
                  const isSelected = selectedIds.includes(app.id);
                  const isDisabled = !isSelected && selectedIds.length >= 2;
                  return (
                    <div
                      key={app.id}
                      onClick={() => !isDisabled && toggleSelect(app.id)}
                      className={'border rounded-xl p-3 cursor-pointer transition-all ' + (
                        isDisabled ? 'opacity-40 cursor-not-allowed border-gray-200' :
                        isSelected ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-200' :
                        'border-gray-200 hover:border-emerald-300'
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div className={'w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center ' + (isSelected ? 'border-emerald-600 bg-emerald-600' : 'border-gray-300')}>
                          {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-gray-900 text-sm">
                            {app.first_name} {app.last_name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {app.profession}{app.city ? ' · ' + app.city : ''}
                          </div>
                          {app.interests?.length > 0 && (
                            <div className="text-xs text-emerald-700 mt-0.5">
                              {app.interests.slice(0, 3).join(' · ')}
                            </div>
                          )}
                        </div>
                        <div className="text-xs text-gray-400 flex-shrink-0">{app.nationality}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Match preview */}
          {selectedApps.length === 2 && (
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
              <div className="text-xs font-semibold text-emerald-700 mb-1">Match Preview</div>
              <div className="text-sm text-emerald-900 font-medium">
                {selectedApps[0].first_name} {selectedApps[0].last_name}
                <span className="text-emerald-400 mx-2">↔</span>
                {selectedApps[1].first_name} {selectedApps[1].last_name}
              </div>
              <div className="text-xs text-emerald-600 mt-1">
                Both will receive a match invitation email shortly after you click Send.
              </div>
            </div>
          )}

          {/* Optional note */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Admin note <span className="text-gray-400 font-normal">(internal — not shared with participants)</span>
            </label>
            <textarea
              value={adminNote}
              onChange={e => setAdminNote(e.target.value)}
              placeholder="Why this pair? Any shared interests or context worth noting..."
              rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
        </div>

        <div className="flex items-center justify-between p-6 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSendInvitations}
            disabled={creating || selectedIds.length !== 2}
            className="flex items-center gap-2 px-5 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <EnvelopeIcon className="w-4 h-4" />
            {creating ? 'Sending invitations…' : 'Send Match Invitations'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const DeepFikaAdmin = () => {
  const [activeTab, setActiveTab] = useState('applications');
  const [loading, setLoading] = useState(false);
  const [applications, setApplications] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  const [showCreateSession, setShowCreateSession] = useState(false);
  const [filters, setFilters] = useState({ status: '', email: '' });
  const [pagination, setPagination] = useState({ total: 0, limit: 30, offset: 0 });
  const [sendingInvite, setSendingInvite] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  // Feedback tab
  const [deepFikaFeedback, setDeepFikaFeedback] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackTotal, setFeedbackTotal] = useState(0);

  // ── Data loading ────────────────────────────────────────────────────────────

  const loadApplications = async () => {
    setLoading(true);
    try {
      const result = await curatedFikaAdminService.getApplications(
        { ...filters, format_type: 'deep_fika' },
        pagination
      );
      if (result.success) {
        const raw = result.data.data || result.data || [];
        // Client-side guard: always filter to deep_fika only regardless of
        // whether the edge function has been redeployed with the format_type filter
        const deepOnly = raw.filter(a => a.format_type === 'deep_fika');
        setApplications(deepOnly);
        if (result.data.pagination) {
          setPagination(prev => ({ ...prev, total: deepOnly.length }));
        }
      }
    } catch (err) {
      console.error('Error loading Deep Fika applications:', err);
    }
    setLoading(false);
  };

  const loadSessions = async () => {
    try {
      const result = await curatedFikaAdminService.getSessions({ session_type: 'deep_fika' });
      if (result.success) {
        const allSessions = result.data?.data || result.data || [];
        // Client-side filter until edge function supports session_type filter
        setSessions(allSessions.filter(s => s.session_type === 'deep_fika'));
      }
    } catch (err) {
      console.error('Error loading sessions:', err);
    }
  };

  const loadStats = async () => {
    try {
      const result = await curatedFikaAdminService.getApplications(
        { format_type: 'deep_fika' },
        { limit: 500, offset: 0 }
      );
      if (result.success) {
        const raw = result.data?.data || result.data || [];
        // Client-side guard: only count deep_fika rows
        const all = raw.filter(a => a.format_type === 'deep_fika');
        setStats({
          total:        all.length,
          submitted:    all.filter(a => a.application_status === 'submitted').length,
          under_review: all.filter(a => a.application_status === 'under_review').length,
          approved:     all.filter(a => a.application_status === 'approved').length,
          rejected:     all.filter(a => a.application_status === 'rejected').length,
        });
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const loadFeedback = async () => {
    setFeedbackLoading(true);
    try {
      const result = await curatedFikaAdminService.getDeepFikaFeedback({ limit: 50, offset: 0 });
      if (result.success) {
        const fb = result.data?.data?.feedback || result.data?.feedback || [];
        setDeepFikaFeedback(fb);
        setFeedbackTotal(result.data?.data?.total || result.data?.total || fb.length);
      }
    } catch (err) {
      console.error('Error loading Deep Fika feedback:', err);
    }
    setFeedbackLoading(false);
  };

  useEffect(() => {
    loadApplications();
    loadSessions();
    loadStats();
  }, []);

  useEffect(() => {
    loadApplications();
  }, [filters]);

  useEffect(() => {
    if (activeTab === 'feedback') loadFeedback();
  }, [activeTab]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleStatusChange = (id, newStatus, newNotes) => {
    setApplications(prev =>
      prev.map(a => a.id === id
        ? { ...a, application_status: newStatus, internal_notes: newNotes }
        : a
      )
    );
    loadStats();
    setSuccessMessage('Status updated.');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleQuickStatus = async (app, status) => {
    try {
      await curatedFikaAdminService.updateApplicationStatus(app.id, status, '');
      handleStatusChange(app.id, status, app.internal_notes);
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const handleSendInvitation = async (sessionId) => {
    setSendingInvite(sessionId);
    try {
      const result = await curatedFikaAdminService.sendSessionInvitations(sessionId);
      if (result.success) {
        setSuccessMessage('Invitations sent!');
        setTimeout(() => setSuccessMessage(''), 4000);
      } else {
        throw new Error(result.error);
      }
    } catch (err) {
      alert('Error sending invitations: ' + err.message);
    }
    setSendingInvite(null);
  };

  const handleSendFeedback = async (sessionId) => {
    if (!window.confirm('Send feedback email to both participants now?')) return;
    setSendingInvite(sessionId + '-feedback');
    try {
      const result = await curatedFikaAdminService.sendDeepFikaFeedbackEmail(sessionId);
      if (result.success) {
        setSuccessMessage('Feedback emails sent!');
        loadSessions();
        setTimeout(() => setSuccessMessage(''), 4000);
      } else {
        throw new Error(result.error);
      }
    } catch (err) {
      alert('Error sending feedback: ' + err.message);
    }
    setSendingInvite(null);
  };

  const handleSessionCreated = () => {
    loadSessions();
    loadStats();
    setSuccessMessage('Match invitations sent!');
    setTimeout(() => setSuccessMessage(''), 4000);
  };

  const approvedApps = applications.filter(a => a.application_status === 'approved');

  // ── UI ──────────────────────────────────────────────────────────────────────

  const TABS = [
    { key: 'applications', label: 'Applications', icon: '📋' },
    { key: 'sessions', label: 'Sessions & Matches', icon: '🫂' },
    { key: 'feedback', label: 'Feedback', icon: '⭐' },
    { key: 'stats', label: 'Stats', icon: '📊' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Modals */}
      {selectedApp && (
        <ApplicationModal
          app={selectedApp}
          onClose={() => setSelectedApp(null)}
          onStatusChange={handleStatusChange}
        />
      )}
      {showCreateSession && (
        <CreateSessionModal
          approvedApps={approvedApps}
          onClose={() => setShowCreateSession(false)}
          onCreated={handleSessionCreated}
        />
      )}

      {/* Page header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                🫂 Deep Fika Admin
                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 text-sm rounded-full font-medium">
                  1-on-1
                </span>
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                Manage applications, create matches, send invitations
              </p>
            </div>

            <div className="flex items-center gap-3">
              {successMessage && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="px-3 py-1.5 bg-emerald-100 text-emerald-800 text-sm rounded-lg font-medium"
                >
                  ✓ {successMessage}
                </motion.div>
              )}
              <button
                onClick={() => { loadApplications(); loadSessions(); loadStats(); if (activeTab === 'feedback') loadFeedback(); }}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
              >
                <ArrowPathIcon className="w-4 h-4" />
                Refresh
              </button>
              <button
                onClick={() => setShowCreateSession(true)}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700"
              >
                <PlusIcon className="w-4 h-4" />
                New Match
              </button>
            </div>
          </div>

          {/* Quick stats */}
          {stats && (
            <div className="grid grid-cols-5 gap-3 mt-5">
              <StatCard label="Total" value={stats.total} icon="📬" color="gray" />
              <StatCard label="Submitted" value={stats.submitted} icon="📥" color="blue" />
              <StatCard label="Under Review" value={stats.under_review} icon="🔍" color="amber" />
              <StatCard label="Approved" value={stats.approved} icon="✅" color="emerald" />
              <StatCard label="Sessions" value={sessions.length} icon="🫂" color="emerald" />
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-1 mt-5 border-b border-gray-200">
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={'px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ' + (
                  activeTab === tab.key
                    ? 'border-emerald-600 text-emerald-700'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                )}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab content */}
      <div className="max-w-6xl mx-auto px-6 py-6">

        {/* ── APPLICATIONS TAB ── */}
        {activeTab === 'applications' && (
          <div>
            {/* Filters */}
            <div className="flex items-center gap-3 mb-5">
              <div className="relative">
                <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by email..."
                  value={filters.email}
                  onChange={e => setFilters(f => ({ ...f, email: e.target.value }))}
                  className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 w-56"
                />
              </div>
              <select
                value={filters.status}
                onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="">All statuses</option>
                <option value="submitted">Submitted</option>
                <option value="under_review">Under Review</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="waitlist">Waitlist</option>
              </select>
              <span className="text-sm text-gray-500">{applications.length} applicant{applications.length !== 1 ? 's' : ''}</span>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
              </div>
            ) : applications.length === 0 ? (
              <div className="text-center py-20 text-gray-400">
                <HeartIcon className="w-16 h-16 mx-auto mb-4 text-gray-200" />
                <p className="text-lg font-medium text-gray-500">No Deep Fika applications yet</p>
                <p className="text-sm mt-1">Applications submitted via /deep-fika/apply will appear here</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Applicant</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Profession</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">City</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Subscription</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Expires</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Status</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Submitted</th>
                      <th className="text-left px-4 py-3 font-semibold text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applications.map((app, i) => (
                      <tr
                        key={app.id}
                        className={'border-b border-gray-100 hover:bg-gray-50 transition-colors ' + (i % 2 === 0 ? '' : 'bg-gray-50/50')}
                      >
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-900">{app.first_name} {app.last_name}</div>
                          <div className="text-xs text-gray-500">{app.email}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">{app.profession || '—'}</td>
                        <td className="px-4 py-3 text-gray-700">{app.city || '—'}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {(() => {
                            const sub = getSubscriptionDisplay(app);
                            return (
                              <span className={'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ' + sub.color}>
                                {sub.icon} {sub.label}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {(() => {
                            const expiry = formatSubscriptionExpiry(app.subscription_expires);
                            return expiry
                              ? <span className={'text-xs ' + expiry.color}>{expiry.text}</span>
                              : <span className="text-xs text-gray-400">—</span>;
                          })()}
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={app.application_status} />
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs">
                          {app.submitted_at
                            ? new Date(app.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
                            : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => setSelectedApp(app)}
                              className="text-gray-400 hover:text-emerald-600 transition-colors"
                              title="View details"
                            >
                              <EyeIcon className="w-4 h-4" />
                            </button>
                            {app.application_status === 'submitted' && (
                              <>
                                <button
                                  onClick={() => handleQuickStatus(app, 'approved')}
                                  className="text-gray-400 hover:text-emerald-600 transition-colors"
                                  title="Approve"
                                >
                                  <CheckCircleIcon className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleQuickStatus(app, 'rejected')}
                                  className="text-gray-400 hover:text-red-500 transition-colors"
                                  title="Reject"
                                >
                                  <XCircleIcon className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Approved pool shortcut */}
            {approvedApps.length >= 2 && (
              <div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center justify-between">
                <div>
                  <span className="font-semibold text-emerald-800">{approvedApps.length} approved applicants</span>
                  <span className="text-emerald-700 text-sm ml-2">ready to be matched</span>
                </div>
                <button
                  onClick={() => setShowCreateSession(true)}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700"
                >
                  Create a Match →
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── SESSIONS TAB ── */}
        {activeTab === 'sessions' && (
          <div>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-semibold text-gray-900">Deep Fika Sessions</h2>
              <button
                onClick={() => setShowCreateSession(true)}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700"
              >
                <PlusIcon className="w-4 h-4" />
                New Match
              </button>
            </div>

            {sessions.length === 0 ? (
              <div className="text-center py-20 text-gray-400">
                <CalendarIcon className="w-16 h-16 mx-auto mb-4 text-gray-200" />
                <p className="text-lg font-medium text-gray-500">No Deep Fika sessions yet</p>
                <p className="text-sm mt-1">Create your first 1-on-1 match above</p>
              </div>
            ) : (
              <div className="space-y-4">
                {sessions.map(session => {
                  const participants = session.curated_fika_participants || [];
                  const matchStatus = session.metadata?.match_status;
                  const mutualAcceptedAt = session.metadata?.mutual_acceptance_at;
                  const isMutuallyAccepted = matchStatus === 'mutually_accepted';
                  const isCompleted = session.session_status === 'completed';

                  return (
                    <div key={session.id} className="bg-white rounded-xl border border-gray-200 p-5">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="font-bold text-gray-900">{session.session_title}</div>
                          <div className="text-sm text-gray-500 mt-1 flex items-center gap-3 flex-wrap">
                            <span>📅 Invitations sent {session.created_at ? new Date(session.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}</span>
                            {isMutuallyAccepted && mutualAcceptedAt && (
                              <span className="text-emerald-700">
                                🤝 Mutually accepted {new Date(mutualAcceptedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                          {/* Match status badge */}
                          {matchStatus ? (
                            <span className={'px-2 py-1 rounded-full text-xs font-semibold ' + (
                              matchStatus === 'mutually_accepted' ? 'bg-emerald-100 text-emerald-800' :
                              matchStatus === 'declined' ? 'bg-red-100 text-red-700' :
                              matchStatus === 'pending' ? 'bg-amber-100 text-amber-800' :
                              'bg-gray-100 text-gray-700'
                            )}>
                              {matchStatus === 'mutually_accepted' ? '🤝 Mutually Accepted' :
                               matchStatus === 'declined' ? '✗ Declined' :
                               matchStatus === 'pending' ? '⏳ Awaiting Response' :
                               matchStatus}
                            </span>
                          ) : (
                            <span className={'px-2 py-1 rounded-full text-xs font-semibold ' + (
                              session.session_status === 'completed' ? 'bg-gray-100 text-gray-700' :
                              session.session_status === 'confirmed' ? 'bg-emerald-100 text-emerald-800' :
                              session.session_status === 'cancelled' ? 'bg-red-100 text-red-700' :
                              'bg-blue-100 text-blue-800'
                            )}>
                              {session.session_status}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Per-participant response status */}
                      {participants.length > 0 && (
                        <div className="mt-3 flex items-center gap-4">
                          {participants.map(p => {
                            const name = p.curated_fika_applications
                              ? (p.curated_fika_applications.first_name + ' ' + p.curated_fika_applications.last_name)
                              : 'Participant';
                            const status = p.participation_status;
                            const icon = status === 'confirmed' ? '✅' : status === 'cancelled' ? '❌' : '⏳';
                            const color = status === 'confirmed' ? 'text-emerald-700' : status === 'cancelled' ? 'text-red-600' : 'text-amber-600';
                            return (
                              <div key={p.id} className="flex items-center gap-1.5">
                                <span className="text-sm">{icon}</span>
                                <span className={'text-xs font-medium ' + color}>{name}</span>
                                <span className="text-xs text-gray-400">({status === 'invited' ? 'pending' : status})</span>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {session.host_notes && (
                        <div className="mt-3 text-xs text-gray-500 bg-gray-50 rounded-lg p-2">
                          📝 {session.host_notes}
                        </div>
                      )}

                      <div className="mt-4 flex items-center gap-2">
                        {/* Manually send feedback for mutually accepted sessions */}
                        {isMutuallyAccepted && !isCompleted && (
                          <button
                            onClick={() => handleSendFeedback(session.id)}
                            disabled={sendingInvite === session.id + '-feedback'}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-semibold hover:bg-amber-700 disabled:opacity-50"
                          >
                            <StarIcon className="w-3.5 h-3.5" />
                            {sendingInvite === session.id + '-feedback' ? 'Sending…' : 'Send Feedback Now'}
                          </button>
                        )}
                        {isCompleted && (
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <CheckCircleIcon className="w-3.5 h-3.5" />
                            Feedback sent · session completed
                          </span>
                        )}
                        {!isMutuallyAccepted && !isCompleted && session.session_status !== 'cancelled' && (
                          <span className="text-xs text-gray-400">
                            Waiting for both to accept the match invitation
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── FEEDBACK TAB ── */}
        {activeTab === 'feedback' && (
          <div>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Deep Fika Feedback</h2>
                <p className="text-sm text-gray-500 mt-0.5">{feedbackTotal} response{feedbackTotal !== 1 ? 's' : ''} received</p>
              </div>
              <button
                onClick={loadFeedback}
                disabled={feedbackLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              >
                <ArrowPathIcon className={'w-4 h-4 ' + (feedbackLoading ? 'animate-spin' : '')} />
                Refresh
              </button>
            </div>

            {feedbackLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
              </div>
            ) : deepFikaFeedback.length === 0 ? (
              <div className="text-center py-20 text-gray-400">
                <StarIcon className="w-16 h-16 mx-auto mb-4 text-gray-200" />
                <p className="text-lg font-medium text-gray-500">No feedback yet</p>
                <p className="text-sm mt-1">Feedback responses will appear here after participants submit them</p>
              </div>
            ) : (
              <div className="space-y-4">
                {deepFikaFeedback.map(item => {
                  const app = item.curated_fika_participants?.curated_fika_applications;
                  const session = item.curated_fika_sessions;
                  const meta = item.metadata?.deep_fika_specific || {};
                  const connectionRating = item.group_chemistry; // stored as group_chemistry
                  const conversationRating = item.conversation_quality;
                  const overallRating = item.overall_satisfaction;
                  const wouldMatchAgain = item.would_attend_again;
                  const openFeedback = meta.open_feedback || item.what_worked_well;

                  const ratingColor = (r) => {
                    if (!r) return 'text-gray-400';
                    if (r >= 4) return 'text-emerald-600';
                    if (r >= 3) return 'text-amber-600';
                    return 'text-red-500';
                  };

                  const starDisplay = (r, max = 5) => r ? ('★'.repeat(r) + '☆'.repeat(max - r)) : '—';

                  return (
                    <div key={item.id} className="bg-white rounded-xl border border-gray-200 p-5">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <div className="font-semibold text-gray-900">
                            {app ? app.first_name + ' ' + app.last_name : 'Anonymous'}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {app?.email}
                            {app?.profession ? ' · ' + app.profession : ''}
                          </div>
                          <div className="text-xs text-gray-400 mt-1">
                            Session: {session?.session_title || '—'}
                          </div>
                        </div>
                        <div className="text-xs text-gray-400 text-right flex-shrink-0 ml-4">
                          <div>Submitted</div>
                          <div>{item.submitted_at ? new Date(item.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' }) : '—'}</div>
                        </div>
                      </div>

                      {/* Ratings */}
                      <div className="grid grid-cols-3 gap-3 mb-4">
                        {[
                          { label: 'Connection', value: connectionRating },
                          { label: 'Conversation', value: conversationRating },
                          { label: 'Overall', value: overallRating },
                        ].map(({ label, value }) => (
                          <div key={label} className="bg-gray-50 rounded-lg p-3 text-center">
                            <div className={'text-lg font-bold ' + ratingColor(value)}>
                              {value ? value + '/5' : '—'}
                            </div>
                            <div className={'text-xs mt-0.5 ' + ratingColor(value)}>
                              {starDisplay(value)}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">{label}</div>
                          </div>
                        ))}
                      </div>

                      {/* Would match again + open feedback */}
                      <div className="flex items-center gap-4 mb-3 flex-wrap">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-600">Match again?</span>
                          {wouldMatchAgain === true && (
                            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs rounded-full font-medium">👍 Yes</span>
                          )}
                          {wouldMatchAgain === false && (
                            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full font-medium">✋ Not now</span>
                          )}
                          {wouldMatchAgain === null && (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-600">Connection made?</span>
                          <span className={'px-2 py-0.5 text-xs rounded-full font-medium ' + (item.connection_made ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600')}>
                            {item.connection_made ? 'Yes' : 'No'}
                          </span>
                        </div>
                      </div>

                      {openFeedback && (
                        <div className="bg-stone-50 border border-stone-200 rounded-lg p-3">
                          <div className="text-xs font-semibold text-stone-600 mb-1">Open feedback</div>
                          <p className="text-sm text-stone-700 leading-relaxed">"{openFeedback}"</p>
                        </div>
                      )}

                      {item.what_could_improve && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
                          <div className="text-xs font-semibold text-amber-700 mb-1">Could improve</div>
                          <p className="text-sm text-amber-800 leading-relaxed">"{item.what_could_improve}"</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── STATS TAB ── */}
        {activeTab === 'stats' && (
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-5">Deep Fika Overview</h2>
            {stats ? (
              <div className="space-y-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard label="Total Applications" value={stats.total} icon="📬" color="gray" />
                  <StatCard label="Approved" value={stats.approved} icon="✅" color="emerald" />
                  <StatCard label="Sessions Created" value={sessions.length} icon="🫂" color="emerald" />
                  <StatCard label="Pending Review" value={stats.submitted + stats.under_review} icon="⏳" color="amber" />
                </div>

                {/* Funnel */}
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Application Funnel</h3>
                  {[
                    { label: 'Submitted',    value: stats.total,        color: 'bg-blue-500' },
                    { label: 'Approved',     value: stats.approved,     color: 'bg-emerald-500' },
                    { label: 'Matched',      value: sessions.length,    color: 'bg-teal-500' },
                  ].map(row => (
                    <div key={row.label} className="flex items-center gap-3 mb-3">
                      <div className="w-28 text-sm text-gray-600">{row.label}</div>
                      <div className="flex-1 h-4 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={'h-full rounded-full ' + row.color}
                          style={{ width: stats.total > 0 ? Math.max(4, (row.value / stats.total) * 100) + '%' : '0%' }}
                        />
                      </div>
                      <div className="w-8 text-sm font-semibold text-gray-900">{row.value}</div>
                    </div>
                  ))}
                </div>

                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
                  <h3 className="font-semibold text-emerald-900 mb-2">Approval rate</h3>
                  <div className="text-3xl font-bold text-emerald-700">
                    {stats.total > 0 ? Math.round((stats.approved / stats.total) * 100) : 0}%
                  </div>
                  <p className="text-sm text-emerald-700 mt-1">
                    {stats.approved} approved out of {stats.total} total applications
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default DeepFikaAdmin;
